/**
 * TS mirror of infrastructure/lambdaFunctions/sharedModules/python/
 * sstshub_rate_templates.py's SSTSHUB_TEMPLATES + select_sstshub_template.
 *
 * Used only to render the volume-derived interchange-plus qualified-discount
 * % placeholders in ManualFeeAdjustmentSection's "Interchange-plus pricing"
 * admin section (pricingSummarySeparation) -- previously these showed a
 * generic "Template default" with no real number, because this data existed
 * only in Python. The CardConnect submission is still built backend-side
 * from the canonical Python table; this mirror is for UX only.
 *
 * IMPORTANT: keep in sync with sstshub_rate_templates.py -- update both
 * together.
 */

export interface SstshubTemplate {
  template: string;
  portfolioPrefix: string;
  minVolume: number;
  /** Fraction (0.0015 == 0.15%); multiply by 100 for a percent number. */
  creditMarkupPct: number;
  creditFeeTxn: number;
  debitMarkupPct: number;
  debitFeeTxn: number;
  creditDebitDiscountPct: number;
  creditDebitDiscountTxn: number;
  /** Already a percent number (0.40 == 0.40%), unlike creditMarkupPct. */
  amexQualified: number;
}

// Ordered low-to-high by qualifying monthly volume, mirroring SSTSHUB_TEMPLATES.
export const SSTSHUB_TEMPLATES: SstshubTemplate[] = [
  {
    template: "SSTHUB 1",
    portfolioPrefix: "WILMA-AI",
    minVolume: 100_000,
    creditMarkupPct: 0.0015,
    creditFeeTxn: 0.1,
    debitMarkupPct: 0.0015,
    debitFeeTxn: 0.1,
    creditDebitDiscountPct: 0.001,
    creditDebitDiscountTxn: 0.01,
    amexQualified: 0.4,
  },
  {
    template: "SSTHUB 2",
    portfolioPrefix: "WILMA-AI",
    minVolume: 200_000,
    creditMarkupPct: 0.001,
    creditFeeTxn: 0.08,
    debitMarkupPct: 0.001,
    debitFeeTxn: 0.08,
    creditDebitDiscountPct: 0.001,
    creditDebitDiscountTxn: 0.01,
    amexQualified: 0.4,
  },
  {
    template: "SSTHUB 3",
    portfolioPrefix: "WILMA-AI",
    minVolume: 300_000,
    creditMarkupPct: 0.0008,
    creditFeeTxn: 0.07,
    debitMarkupPct: 0.0008,
    debitFeeTxn: 0.07,
    creditDebitDiscountPct: 0.001,
    creditDebitDiscountTxn: 0.01,
    amexQualified: 0.4,
  },
  {
    template: "SSTHUB 4",
    portfolioPrefix: "WILMA-AI",
    minVolume: 400_000,
    creditMarkupPct: 0.0007,
    creditFeeTxn: 0.06,
    debitMarkupPct: 0.0007,
    debitFeeTxn: 0.06,
    creditDebitDiscountPct: 0.001,
    creditDebitDiscountTxn: 0.01,
    amexQualified: 0.4,
  },
  {
    template: "SSTHUB 5",
    portfolioPrefix: "WILMA-AI",
    minVolume: 500_000,
    creditMarkupPct: 0.0006,
    creditFeeTxn: 0.05,
    debitMarkupPct: 0.0006,
    debitFeeTxn: 0.05,
    creditDebitDiscountPct: 0.001,
    creditDebitDiscountTxn: 0.01,
    amexQualified: 0.4,
  },
  {
    template: "SSTHUB 6",
    portfolioPrefix: "WILMA-AI",
    minVolume: 600_000,
    creditMarkupPct: 0.0005,
    creditFeeTxn: 0.05,
    debitMarkupPct: 0.0005,
    debitFeeTxn: 0.05,
    creditDebitDiscountPct: 0.001,
    creditDebitDiscountTxn: 0.01,
    amexQualified: 0.4,
  },
  {
    template: "SSTHUB 10",
    portfolioPrefix: "WILMA-AI2",
    minVolume: 700_000,
    creditMarkupPct: 0.0004,
    creditFeeTxn: 0.04,
    debitMarkupPct: 0.0004,
    debitFeeTxn: 0.04,
    creditDebitDiscountPct: 0.0015,
    creditDebitDiscountTxn: 0.015,
    amexQualified: 0.25,
  },
  {
    template: "SSTHUB 20",
    portfolioPrefix: "WILMA-AI2",
    minVolume: 1_000_000,
    creditMarkupPct: 0.0003,
    creditFeeTxn: 0.03,
    debitMarkupPct: 0.0003,
    debitFeeTxn: 0.03,
    creditDebitDiscountPct: 0.0015,
    creditDebitDiscountTxn: 0.015,
    amexQualified: 0.25,
  },
  {
    template: "SSTHUB 30",
    portfolioPrefix: "WILMA-AI2",
    minVolume: 1_500_000,
    creditMarkupPct: 0.0002,
    creditFeeTxn: 0.02,
    debitMarkupPct: 0.0002,
    debitFeeTxn: 0.02,
    creditDebitDiscountPct: 0.0015,
    creditDebitDiscountTxn: 0.015,
    amexQualified: 0.25,
  },
];

/**
 * Return the SSTHUB template matching `monthlyVolume`, or null.
 *
 * Mirrors select_sstshub_template's selection rules: missing/non-numeric
 * volume -> null; below the lowest tier -> the entry template (the most
 * conservative rate); otherwise the highest tier whose minVolume the merchant
 * meets.
 *
 * `portfolioPrefix` confines selection to one portfolio's rows ("WILMA-AI" or
 * "WILMA-AI2") so the previewed rate schedule follows the effective sales
 * code's side instead of being chosen by volume across BOTH portfolios
 * (option A). Undefined/null preserves the original volume-only behavior. The
 * floor/cap clamp is applied WITHIN the chosen portfolio. An unknown prefix
 * matching no rows falls back to the full table so the function stays total.
 */
export function selectSstshubTemplate(
  monthlyVolume: string | number | null | undefined,
  portfolioPrefix?: string | null,
): SstshubTemplate | null {
  if (monthlyVolume == null || monthlyVolume === "") return null;
  const volume =
    typeof monthlyVolume === "number" ? monthlyVolume : Number(monthlyVolume);
  if (!Number.isFinite(volume)) return null;
  let rows = SSTSHUB_TEMPLATES;
  if (portfolioPrefix != null) {
    const filtered = SSTSHUB_TEMPLATES.filter(
      (t) => t.portfolioPrefix === portfolioPrefix,
    );
    if (filtered.length > 0) rows = filtered;
  }
  if (volume < rows[0].minVolume) return rows[0];
  let selected = rows[0];
  for (const tmpl of rows) {
    if (volume >= tmpl.minVolume) selected = tmpl;
    else break;
  }
  return selected;
}

export interface IcPlusPricingBase {
  visaQualCreditDiscountPct: number;
  mastercardQualCreditDiscountPct: number;
  discoverQualCreditDiscountPct: number;
  amexOptBlueQualDiscountPct: number;
}

/**
 * The volume-derived base for the four qualified-discount % fields, before
 * any admin override -- mirrors cardConnectStartHandler's/sharedModules'
 * credit_markup_qual_pct = round(credit_markup_pct * 100, 4) conversion
 * (Visa/Mastercard/Discover share the same credit markup) and amex_qualified
 * used as-is. Returns null when no template resolves (no/invalid volume).
 */
export function icPlusPricingBase(
  monthlyVolume: string | number | null | undefined,
  portfolioPrefix?: string | null,
): IcPlusPricingBase | null {
  const template = selectSstshubTemplate(monthlyVolume, portfolioPrefix);
  if (!template) return null;
  const baseCredit =
    Math.round(template.creditDebitDiscountPct * 100 * 10_000) / 10_000;
  return {
    visaQualCreditDiscountPct: baseCredit,
    mastercardQualCreditDiscountPct: baseCredit,
    discoverQualCreditDiscountPct: baseCredit,
    amexOptBlueQualDiscountPct: template.amexQualified,
  };
}
